import {
  computed,
  Injectable,
  resource,
  ResourceRef,
  Signal,
} from '@angular/core';
import { DEFAULT_SALUTATIONS, INormalText, ISalutation } from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { firstValueFrom } from 'rxjs';
import { SalutationGateway } from './salutation.gateway';

@Injectable({
  providedIn: 'root',
})
export class SalutationStore {
  protected readonly allChannels: boolean = false;
  private readonly gateway: SalutationGateway = new SalutationGateway();

  readonly #salutationResource: ResourceRef<
    IApiResponse<ISalutation[]> | undefined
  > = resource({
    loader: (): Promise<IApiResponse<ISalutation[]>> => {
      return firstValueFrom(this.gateway.list(this.allChannels));
    },
  });

  readonly isLoading: Signal<boolean> = computed((): boolean =>
    this.#salutationResource.isLoading()
  );
  readonly salutations: Signal<ISalutation[]> = computed((): ISalutation[] =>
    this.#salutationResource.hasValue() &&
    this.#salutationResource.value()?.data &&
    this.#salutationResource.value()!.data!.length > 0
      ? this.#salutationResource.value()!.data!
      : DEFAULT_SALUTATIONS
  );

  readonly salutationOptions: Signal<INormalText[]> = computed(
    (): INormalText[] => {
      return (
        this.salutations().reduce(
          (acc: INormalText[], curr: ISalutation): INormalText[] =>
            acc.concat(curr.texts),
          []
        ) ?? []
      );
    }
  );

  getId(value: string | undefined, fallback = ''): string {
    return String(this.find(value)?.id ?? fallback);
  }

  getTexts(value: string | undefined): INormalText[] {
    return this.find(value)?.texts ?? [];
  }

  reloadSalutations(): void {
    this.#salutationResource.reload();
  }

  updateSalutations(query: ISalutation[]): void {
    this.#salutationResource.update((): IApiResponse<ISalutation[]> => {
      return {
        data: query,
      };
    });
  }

  async save(query: ISalutation[]): Promise<void> {
    const result: IApiResponse<ISalutation[]> = await firstValueFrom(
      this.gateway.save(query)
    );
    if (result?.data) {
      this.updateSalutations(result.data);
    }
  }

  async delete(query: string, reload = true): Promise<void> {
    await firstValueFrom(this.gateway.delete(query));
    if (reload) {
      this.reloadSalutations();
    }
  }

  private find(value: string | undefined): ISalutation | undefined {
    if (!value) return undefined;

    return this.salutations().find(
      salutation =>
        salutation.id === value ||
        salutation.texts.some(text =>
          text.text.values.some(languageValue => languageValue.value === value)
        )
    );
  }
}

/** Separate cache for editing all channels in the CMS. */
@Injectable({ providedIn: 'root' })
export class CmsSalutationStore extends SalutationStore {
  protected override readonly allChannels: boolean = true;
}
